<script lang="ts" setup>
import { NuxtLink } from '#components';
import { Button } from '~ds/components/ui/button';
import { Card } from '~ds/components/ui/card';
import { Heading, Text } from '~ds/components/ui/typography';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { ArticleFeature } from './types';

import { contentCardPicture } from './contentCardPicture';

/**
 * The article that leads a page: its cover beside the title, standfirst and byline, with a button
 * that opens it. The cover sits above the copy on small screens.
 */
interface Props {
  article: ArticleFeature;
}

const props = defineProps<Props>();

const emit = defineEmits<{ select: [] }>();

// The lead's cover is the page's hero, so it is preloaded like a featured block's lead card.
const picture = contentCardPicture('lead');

const { t } = useI18n();

const byline = computed(() => [
  props.article.authorName ? t('unified.articles.byAuthor', { name: props.article.authorName }) : null,
  props.article.publishedOn,
  props.article.readingTimeMinutes ? t('unified.articles.minRead', { count: props.article.readingTimeMinutes }) : null,
].filter(Boolean).join(' · '));
</script>

<template>
  <article
    class="grid items-center gap-6 md:grid-cols-2 md:gap-10"
    data-testid="article-feature"
  >
    <Card
      variant="media"
      class="aspect-3/2"
    >
      <NuxtPicture
        :src="article.imageUrl"
        format="avif,webp"
        alt=""
        v-bind="picture"
      />
    </Card>
    <div class="flex flex-col gap-3">
      <Heading as="h2" size="lg">
        {{ article.name }}
      </Heading>
      <Text
        v-if="article.standfirst"
        class="line-clamp-3 text-muted-foreground"
        data-testid="article-feature-standfirst"
      >
        {{ article.standfirst }}
      </Text>
      <Text
        v-if="byline"
        size="sm"
        class="text-muted-foreground"
        data-testid="article-feature-byline"
      >
        {{ byline }}
      </Text>
      <Button
        size="lg"
        class="mt-3 self-start"
        as-child
      >
        <NuxtLink
          :to="article.to"
          data-testid="article-feature-link"
          @click="emit('select')"
        >
          <i18n-t keypath="unified.articles.readArticleNamed">
            <template #title>
              <span class="sr-only" data-testid="article-feature-link-title">{{ article.name }}</span>
            </template>
          </i18n-t>
        </NuxtLink>
      </Button>
    </div>
  </article>
</template>
